/**
 * `/messages/:id` — one message, opened.
 *
 * **Opening it marks it read**, here and not on the server: a GET changes
 * nothing there, by design, so it is this screen that says "the reader saw it".
 * Only for a recipient's copy — the sender has none, and the server marks the
 * difference by sending `recipients` to the sender alone.
 *
 * The body is rendered as text with its line breaks kept. Never as HTML: it is
 * whatever another person typed.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import PersonIcon from '@mui/icons-material/Person';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MessageDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityDetail } from '../../components/EntityDetail';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { archive, getMessage, markRead, markUnread, unarchive } from './api';

/** A recipient's own copy, as opposed to the sender's view of what they sent. */
function isRecipientCopy(message: MessageDto): boolean {
  return message.recipients === null;
}

export default function MessagePage() {
  const { t } = useTranslation(['messaging', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const messageId = Number(id);

  const state = useAsync(() => getMessage(messageId), [messageId]);

  // Once per message: StrictMode runs effects twice in development, and a
  // second POST would be harmless but pointless.
  const markedFor = useRef<number | null>(null);
  useEffect(() => {
    const message = state.data?.message;
    if (!message || !isRecipientCopy(message) || message.readAt !== null) return;
    if (markedFor.current === message.id) return;
    markedFor.current = message.id;
    void markRead(message.id)
      .then(() => state.setData({ message: { ...message, readAt: new Date().toISOString() } }))
      .catch(() => {
        // Not worth an error over a message that is already on screen; it
        // stays unread in the list, which is the truth.
      });
  }, [state]);

  const change = useSubmit(async (operation: (id: number) => Promise<unknown>, after: 'back' | 'reload') => {
    await operation(messageId);
    if (after === 'back') navigate('/messages');
    else state.reload();
  });

  return (
    <AsyncContent state={state}>
      {({ message }) => {
        const copy = isRecipientCopy(message);
        return (
          <EntityDetail
            backTo={copy && message.archivedAt !== null ? '/messages?box=archived' : copy ? '/messages' : '/messages?box=sent'}
            backLabel={t('detail.back')}
            title={message.subject}
            badges={message.archivedAt !== null && <Chip size="small" label={t('detail.archived')} />}
            description={
              <>
                {t('detail.from', { name: message.sender.displayName })} · <Timestamp value={message.sentAt} />
              </>
            }
            error={change.error}
            actions={
              copy && (
                <Stack direction="row" sx={{ gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<MarkEmailUnreadOutlinedIcon />}
                    disabled={change.pending}
                    onClick={() => void change.submit(markUnread, 'back')}
                  >
                    {t('action.markUnread')}
                  </Button>
                  {message.archivedAt === null ? (
                    <Button
                      variant="outlined"
                      startIcon={<ArchiveOutlinedIcon />}
                      disabled={change.pending}
                      onClick={() => void change.submit(archive, 'back')}
                    >
                      {t('action.archive')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<UnarchiveOutlinedIcon />}
                      disabled={change.pending}
                      onClick={() => void change.submit(unarchive, 'reload')}
                    >
                      {t('action.unarchive')}
                    </Button>
                  )}
                </Stack>
              )
            }
          >
            <Stack spacing={2}>
              {message.recipients ? (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.to')}
                  </Typography>
                  {message.recipients.map((recipient) => (
                    <Chip key={recipient.id} size="small" icon={<PersonIcon />} label={recipient.displayName} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('detail.toCount', { count: message.recipientCount })}
                </Typography>
              )}

              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {message.body}
                </Typography>
              </Paper>
            </Stack>
          </EntityDetail>
        );
      }}
    </AsyncContent>
  );
}
